/**
 * useTokenBalance -- Hook lay so du token tu Supabase
 */

import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../services/supabase/supabaseClient';
import { useAuth } from '../contexts/AuthContext';

interface TokenBalance {
  balance: number;
  totalUsed: number;
  totalPurchased: number;
}

export function useTokenBalance() {
  const { user } = useAuth();
  const [tokenBalance, setTokenBalance] = useState<TokenBalance | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const fetchBalance = useCallback(async () => {
    if (!user) {
      setTokenBalance(null);
      setIsLoading(false);
      return;
    }

    try {
      const { data, error } = await supabase
        .from('token_balance')
        .select('credits, credits_used, credits_purchased')
        .eq('user_id', user.id)
        .single();

      if (error) {
        console.warn('[TOKEN] Could not fetch balance:', error.message);
        setTokenBalance(null);
      } else {
        setTokenBalance({
          balance: data.credits,
          totalUsed: data.credits_used,
          totalPurchased: data.credits_purchased,
        });
      }
    } catch {
      setTokenBalance(null);
    }
    setIsLoading(false);
  }, [user]);

  useEffect(() => {
    fetchBalance();
  }, [fetchBalance]);

  /**
   * Cap nhat balance tu response API (khong can goi Supabase lai)
   */
  const updateBalanceFromResponse = useCallback((newBalance: number) => {
    setTokenBalance(prev => prev ? { ...prev, balance: newBalance } : null);
  }, []);

  /**
   * Format so token de hien thi
   */
  const formatBalance = useCallback((balance: number): string => {
    if (balance >= 1_000_000) return `${(balance / 1_000_000).toFixed(1)}M`;
    if (balance >= 1_000) return `${(balance / 1_000).toFixed(0)}K`;
    return balance.toString();
  }, []);

  const isLow = tokenBalance ? tokenBalance.balance < 10000 : false;
  const isEmpty = tokenBalance ? tokenBalance.balance <= 0 : false;

  return {
    tokenBalance,
    isLoading,
    isLow,
    isEmpty,
    refetch: fetchBalance,
    updateBalanceFromResponse,
    formatBalance,
  };
}
